<template>
	<div class="transaction" :class="{ selected }">
		<header>
			<button v-if="removable" type="button" class="remove" :disabled="removeDisabled" @click.stop="emit('remove')">
				<AppIcon :name="IconName.CloseSmall" :size="12" :stroke-width="1.3" />
			</button>
			<div class="price">
				<span class="currency">$</span>
				<span class="amount">{{ formatQuantity(roundTo(transaction.deal?.salePrice, 1)) }}</span>
			</div>
			<div class="title">
				<h3>{{ addressLines.street || subtitle || $t("unknownAddress") }}</h3>
				<div v-if="addressLines.street && subtitle" class="borough">{{ subtitle }}</div>
			</div>
		</header>
		<dl>
			<div>
				<dt>{{ $t("transaction.inscriptionNumber") }}</dt>
				<dd>{{ transaction.inscriptionNumber }}</dd>
			</div>
			<div>
				<dt>{{ $t("transaction.area") }}</dt>
				<dd>{{ formatArea(transaction.location?.area) }} {{ areaUnitSymbol }}</dd>
			</div>
			<div>
				<dt>{{ $t("transaction.transactionDate") }}</dt>
				<dd>{{ transaction.deal?.registryDate?.toLocaleDateString("en-CA") }}</dd>
			</div>
		</dl>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { formatQuantity, locationSubtitle, roundTo, splitTransactionAddress } from "@/utils/format"
import { AreaUnit } from "@/utils/unitSystem"

const props = defineProps({
	transaction: {
		type: Object,
		default: () => ({ id: 1, name: "nom" }),
	},
	selected: {
		type: Boolean,
		default: false,
	},
	removable: {
		type: Boolean,
		default: false,
	},
	removeDisabled: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits<{
	(e: "remove"): void
}>()

const { areaUnitSymbol, convertAreaToPreferredUnit } = useUnitSystem()

const addressLines = computed(() =>
	splitTransactionAddress(
		props.transaction.location?.address,
		props.transaction.location?.borough,
		props.transaction.location?.municipality
	)
)

// keep the subtitle to just borough/municipality here, unlike the detailed card which also
// shows province/postal code when they're embedded in the address
const subtitle = computed(() =>
	locationSubtitle(props.transaction.location?.borough, props.transaction.location?.municipality)
)

function formatArea(value: number | null | undefined): string {
	if (value == null || !Number.isFinite(value)) {
		return ""
	}

	const convertedArea = convertAreaToPreferredUnit(value, AreaUnit.SquareFeet)

	return formatQuantity(Math.round(convertedArea))
}
</script>

<style scoped>
.transaction {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
	padding: var(--spacing-md);
	box-sizing: border-box;
	border: var(--border-width-base) solid var(--neutral-400);
	border-radius: var(--border-radius-xl);
	cursor: pointer;
	opacity: 0.7;
	transition: all 0.2s ease;

	&.selected {
		border-color: var(--primary-green);
		opacity: 1;
	}

	header {
		position: relative;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);

		.remove {
			position: absolute;
			top: 0;
			right: 0;
			flex-shrink: 0;
			width: 20px;
			height: 20px;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 0;
			border: none;
			background: none;
			border-radius: var(--border-radius-sm);
			color: var(--neutral-600);
			cursor: pointer;
			transition: all 0.2s ease;

			&:hover:not(:disabled) {
				color: var(--primary-green);
				background-color: var(--neutral-200);
			}

			&:disabled {
				opacity: 0.5;
				cursor: not-allowed;
			}
		}

		.price {
			display: flex;
			padding-right: var(--spacing-xl);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-medium);
			font-weight: var(--font-weight-semibold);

			.currency {
				color: var(--primary-green);
				padding-right: var(--spacing-sm);
			}

			.amount {
				color: var(--primary-green);
			}
		}

		.title {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xxs);
			min-width: 0;
		}

		h3 {
			font-family: var(--font-family-primary);
			font-weight: var(--font-weight-medium);
			font-size: var(--font-size-regular);
			color: var(--neutral-900);
			margin: 0;
		}

		.borough {
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			color: var(--neutral-600);
		}
	}

	dl {
		display: flex;
		flex-direction: column;
		margin: 0;

		div {
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			align-items: baseline;
			padding: var(--spacing-xs);
			border-bottom: var(--border-width-thin) solid var(--neutral-200);
			line-height: var(--line-height-relaxed);
			font-size: var(--font-size-small);

			&:last-child {
				border-bottom: none;
			}

			dt {
				font-family: var(--font-family-primary);
				color: var(--neutral-600);
				margin: 0;
			}

			dd {
				font-family: var(--font-family-primary);
				color: var(--neutral-900);
				margin: 0;
				text-align: right;
			}
		}
	}
}
</style>
